import { useState, useCallback } from 'react';
import { apiClient } from '../index';

interface FileChange {
  id: string;
  filename: string;
  status: 'added' | 'modified' | 'deleted';
  modifiedOn: string;
  ipAddress?: string;
}

interface ApiResponse {
  data: FileChange[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const useFileChanges = () => {
  const [data, setData] = useState<FileChange[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFileChanges = useCallback(async (page = 1, filter = '') => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        page: page.toString(),
        limit: '10',
        filter
      };
      
      const result = await apiClient.get<ApiResponse>('/api/file-changes', params);
      setData(result.data);
      setPagination(result.pagination);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch file changes');
    } finally {
      setLoading(false);
    }
  }, []);

  const downloadFile = useCallback(async (fileId: string, filename: string) => {
    // For file downloads, we need to use fetch directly since it returns a blob
    const response = await fetch(`${apiClient['baseUrl']}/api/file-changes/${encodeURIComponent(fileId)}/download`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
      },
    });
    
    if (!response.ok) throw new Error('Download failed');
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  }, []);

  return {
    data,
    pagination,
    loading,
    error,
    fetchFileChanges,
    downloadFile,
  };
};